import { logger } from '../lib/utils/logger';
/**
 * Error boundary specifically for React Query errors with retry functionality
 */
import type { ReactNode } from 'react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from './ErrorBoundary';
import { categorizeError, ErrorType, type AppError } from '../utils/error-utils';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

/**
 * Combines React Query's error reset boundary with our custom error boundary
 * to provide better error handling for data fetching errors
 */
export function QueryErrorBoundary({ children }: Props) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onError={(error, errorInfo) => {
            // Don't double-log authentication errors — the session /
            // directive path owns them. Use the central classifier rather than
            // brittle message-substring matching (CU-868kykvrx).
            const isAuthError = categorizeError(error).type === ErrorType.AUTHENTICATION;

            if (!isAuthError) {
              // Additional error handling for non-auth query errors
              logger.error('Query error caught:', error, errorInfo);
            }
          }}
          fallback={(error: AppError, retry: () => void) => (
            <QueryErrorFallback
              error={error}
              retry={() => {
                // Reset React Query error boundary
                reset();
                // Retry the component
                retry();
              }}
            />
          )}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

interface QueryErrorFallbackProps {
  error: AppError;
  retry: () => void;
}

function QueryErrorFallback({ error, retry }: QueryErrorFallbackProps) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h6 className="text-base font-semibold text-red-900 dark:text-red-100 mb-1">
            Failed to load data
          </h6>
          <p className="text-sm text-red-700 dark:text-red-300">{error.message}</p>
        </div>
        <button
          onClick={retry}
          className="flex items-center gap-1 px-3 py-1.5 text-sm
                     text-red-600 dark:text-red-400
                     hover:bg-red-100 dark:hover:bg-red-900/40
                     focus:outline-hidden focus:ring-2 focus:ring-red-200
                     rounded-lg transition-colors font-medium shrink-0"
        >
          <RefreshCw className="h-3 w-3" />
          Try again
        </button>
      </div>
    </div>
  );
}
